import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";

import {
    container, stylizationHeader, backProfile, arrowBackProfile, formTop, formBottom,
    title, formBreak, formButton, inputTop, textBackProfile, occasionWrapper, occasionItem,
    titleWrapper, postCardContent, titleContent, imgBox, ratio
} from './generator-start-fields.module.scss';
import Arrow from "../../../assets/images/svg/arrow.svg";
import { priceFilters, selectFiltersConfigs } from "../config";
import { EGeneratorFilterKeys, IGeneratorFormValues, TGeneratorFilters } from "../../../models/generator.model";
import { getAbsolutePath } from "../../utility/routes.utility";

import Title from "../../../components/atoms/title";
import Button from "../../../components/atoms/button";
import Input from "../../../components/atoms/form/input";
import Select from "../../../components/atoms/form/select";
import { useFormikContext } from "formik";
import { TOption } from "../../../models/option.model";
import RatioImage from "../../../components/atoms/ratio-image";

interface IGeneratorStartFieldsProps {
    className?: string;
    filters: TGeneratorFilters;
    onOccasionSelect: (values: IGeneratorFormValues) => void;
}

const GeneratorStartFields: React.FC<IGeneratorStartFieldsProps> = ({
    className = '',
    filters,
    onOccasionSelect,
}) => {
    const { t } = useI18next();
    const { setValues, values } = useFormikContext<IGeneratorFormValues>();

    const [occasionImages, setOccasionImages] = useState<string[]>([]);


    useEffect(() => {
        const loadImages = async () => {
            const images: string[] = await Promise.all(
                filters['occasion']
                .slice()
                .sort((a, b) => {
                    if (a.label === "Oczaruj mnie") return 1;  // Przesuń "Oczaruj mnie" na koniec
                    if (b.label === "Oczaruj mnie") return -1; // Przesuń "Oczaruj mnie" na koniec
                    return a.label.localeCompare(b.label); // Standardowe sortowanie alfabetyczne
                })
                .map(async (option) => {
                    const image = await import(`../../../assets/images/occasion/${getOptionValue(option)}.jpg`);
                    return image.default;
                })
            );
            setOccasionImages(images);
        };
        loadImages();
    }, [JSON.stringify(filters)]);

    const getOptionValue = (option: TOption): string | number => {
        return option.id !== undefined ? option.id : option.value;
    };

    const getFallbackValue = (filterKey: EGeneratorFilterKeys): string | number => {
        const options = filters[filterKey] || [];
        const appliedOption = options.find((filterOption) => filterOption.applied);
        const fallbackOption = appliedOption || options[0];

        return fallbackOption ? getOptionValue(fallbackOption) : "";
    };

    const getFallbackPriceValue = (filterKey: EGeneratorFilterKeys): string | number => {
        const options = filters[filterKey] || [];
        const appliedOption = options.find((filterOption) => filterOption.applied);
        const fallbackOption = appliedOption || options[1] || options[0];

        return fallbackOption ? getOptionValue(fallbackOption) : "";
    };

    const getFallbackMultiValue = (filterKey: EGeneratorFilterKeys): string[] | number[] => {
        const options = filters[filterKey] || [];
        const appliedOptions = options.filter((filterOption) => filterOption.applied);
        const selectedOptions = appliedOptions.length ? appliedOptions : options;

        return selectedOptions.map(getOptionValue).filter((value) => !!value) as string[] | number[];
    };

    const occasionSelect = async (option: TOption) => {
        const nextValues: IGeneratorFormValues = {
            ...values,
            occasion: getOptionValue(option),
            colors: values.colors?.length
                ? values.colors
                : getFallbackMultiValue(EGeneratorFilterKeys.colors),
            patterns: values.patterns?.length
                ? values.patterns
                : getFallbackMultiValue(EGeneratorFilterKeys.patterns),
            style: values.style || getFallbackValue(EGeneratorFilterKeys.style),
            seasons: values.seasons || getFallbackValue(EGeneratorFilterKeys.seasons),
            priceTop: values.priceTop || getFallbackPriceValue(EGeneratorFilterKeys.priceTop),
            priceBottom: values.priceBottom || getFallbackPriceValue(EGeneratorFilterKeys.priceBottom),
            priceOuterwear: values.priceOuterwear || getFallbackPriceValue(EGeneratorFilterKeys.priceOuterwear),
            priceOverall: values.priceOverall || getFallbackPriceValue(EGeneratorFilterKeys.priceOverall),
            name: option.label,
        };
        await setValues(nextValues, false);
        onOccasionSelect(nextValues);
    }

    return (
        <div className={`${className} ${container}`}>
            <header className={stylizationHeader}>
                <h1>Wybierz okazję</h1>
            </header>
            <div className={occasionWrapper}>
                {filters["occasion"]
                    .slice()
                    .sort((a, b) => {
                        if (a.label === "Oczaruj mnie") return 1;
                        if (b.label === "Oczaruj mnie") return -1;
                        return a.label.localeCompare(b.label);
                    })
                    .map((option, index) => {
                        const imageSrc = occasionImages[index % occasionImages.length];
                        return (
                            <button
                                type="button"
                                className={occasionItem}
                                key={getOptionValue(option)}
                                onClick={() => occasionSelect(option)}
                            >
                                <span className={imgBox}>
                                    <img src={imageSrc} alt="" />
                                </span>
                                <span className={titleWrapper}>
                                    <span className={titleContent}>{option.label}</span>
                                </span>
                            </button>
                        );
                    })}
            </div>
            <div className={formTop}>
                <Input
                    name="name"
                    type="text"
                    placeholder={t('generator.form.placeholder.name')}
                    label={t('generator.form.label.name')}
                    className={inputTop}
                    theme="transparent"
                    showLabel={true}
                />
                {selectFiltersConfigs.map((config) => {
                    const { isMultiselect, filterKey } = config;
                    return (
                        <Select
                            key={filterKey}
                            options={filters[filterKey]}
                            name={filterKey}
                            label={t(`generator.form.label.${filterKey}`)}
                            placeholder={t(`generator.form.placeholder.${filterKey}`)}
                            isMultiselect={isMultiselect}
                            showCounter={isMultiselect}
                            colorTheme="light"
                            showLabel={true}
                        />
                    );
                })}
            </div>
            {/* <p className={formBreak}>{t('generator.prices')}</p> */}
            <div className={formBottom}>
                {priceFilters.map((filterKey) => {
                    return (
                        <Select
                            key={filterKey}
                            options={filters[filterKey]}
                            name={filterKey}
                            colorTheme="light"
                            label={t(`generator.form.label.${filterKey}`)}
                            placeholder={t(`generator.form.placeholder.${filterKey}`)}
                            showLabel={true}
                        />
                    );
                })}
            </div>
            <Button
                htmlType="submit"
                type="filled"
                color="primary"
                className={formButton}
            >
                {t('generator.button.generate')}
            </Button>
            {/* <Link
                to={getAbsolutePath('ACCOUNT_STYLIZATIONS')}
                className={backProfile}
            >
                <Arrow className={arrowBackProfile} />
                <p className={textBackProfile}>{t('generator.back')}</p>
            </Link> */}
        </div>
    );
};

export default GeneratorStartFields;
